import { RedirectUri } from "../../enumStrings";

/**
 * Monday 13301576985 (spec 12897150351, Aug 25 2026): the Chamber Member Pro
 * Member Dashboard login is memberdashboard.chambermemberpro.com/<chamber slug>.
 * Every rule here applies ONLY on that host. On dashboard.chamberperksapp.com
 * (and every other host) the app behaves exactly as before.
 *
 * On the member host, signed out:
 * - "/"                   -> page that asks for the chamber's own link (no login form)
 * - "/<slug>"             -> the branded member login of that chamber
 * - "/<slug>/<app route>" -> the branded login; after sign-in, the app route
 * - "/<app route>"        -> the branded login of the last chamber used on this
 *                            browser, then the app route; else the "/" page
 * - "/login"              -> same as above (no unbranded login on this host)
 * - "/memberlogin?chamberId=" and the public pages keep working.
 */
export const MEMBER_DASHBOARD_HOST = "memberdashboard.chambermemberpro.com";
const LOCAL_MEMBER_DASHBOARD_HOST = "memberdashboard.localhost";
const REMEMBERED_SLUG_KEY = "cmpMemberDashboardSlug";
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const isMemberDashboardHost = (
  host: string = typeof window === "undefined" ? "" : window.location.host,
  nodeEnv: string | undefined = process.env.NODE_ENV
): boolean => {
  const hostname = String(host || "").toLowerCase().split(":")[0];
  if (hostname === MEMBER_DASHBOARD_HOST) return true;
  // Local QA only: http://memberdashboard.localhost:<port>/<slug>
  return nodeEnv !== "production" && hostname === LOCAL_MEMBER_DASHBOARD_HOST;
};

/** First path segments the app already uses. A slug never shadows them. */
const getAppRouteSegments = (): Set<string> =>
  new Set(
    Object.values(RedirectUri)
      .map((route) => String(route).split("/")[1] || "")
      .filter(Boolean)
      .map((segment) => segment.toLowerCase())
  );

export type MemberDashboardHostPath =
  | { kind: "bare" }
  | { kind: "slug"; slug: string; nextPath: string }
  | { kind: "route" };

/** Reads a path on the member host. */
export const parseMemberDashboardHostPath = (
  pathname: string,
  search = ""
): MemberDashboardHostPath => {
  const segments = String(pathname || "/")
    .split("/")
    .filter(Boolean);
  if (!segments.length) return { kind: "bare" };
  const first = segments[0].toLowerCase();
  if (getAppRouteSegments().has(first)) return { kind: "route" };
  if (!SLUG_PATTERN.test(first)) return { kind: "route" };
  const rest = segments.slice(1).join("/");
  return {
    kind: "slug",
    slug: first,
    nextPath: rest ? getSafeNextPath(`/${rest}${search}`) : "",
  };
};

// Sign-in pages are never the page to open after sign-in.
const NOT_NEXT_PAGES = new Set([
  "login",
  "memberlogin",
  "logout",
  "newpassword",
  "forgotpassword",
  "error",
]);

/** A same-app path only: never another site, never a slug page. */
export const getSafeNextPath = (value: unknown): string => {
  const path = String(value || "");
  if (!path.startsWith("/") || path.startsWith("//") || /[\\\s]/.test(path)) {
    return "";
  }
  const first = (path.split(/[/?#]/)[1] || "").toLowerCase();
  if (!getAppRouteSegments().has(first) || NOT_NEXT_PAGES.has(first)) {
    return "";
  }
  return path;
};

export const rememberMemberDashboardSlug = (slug: string): void => {
  try {
    if (SLUG_PATTERN.test(slug)) localStorage.setItem(REMEMBERED_SLUG_KEY, slug);
  } catch {
    // Storage can be blocked. The slug link in the email still works.
  }
};

export const getRememberedMemberDashboardSlug = (): string => {
  try {
    const slug = localStorage.getItem(REMEMBERED_SLUG_KEY) || "";
    return SLUG_PATTERN.test(slug) ? slug : "";
  } catch {
    return "";
  }
};

/**
 * Where useInitialAuth sends the browser on the member host. Null: stay on
 * this page. Undefined: not the member host, use the existing rules.
 */
export const getMemberDashboardHostRedirect = ({
  host,
  pathname,
  search = "",
  signedIn,
  rememberedSlug,
  nodeEnv,
}: {
  host: string;
  pathname: string;
  search?: string;
  signedIn: boolean;
  rememberedSlug: string;
  nodeEnv?: string;
}): string | null | undefined => {
  if (!isMemberDashboardHost(host, nodeEnv)) return undefined;
  const path = parseMemberDashboardHostPath(pathname, search);
  const urlPath = String(pathname || "/")
    .toLowerCase()
    .replace(/(.+)\/$/, "$1");

  if (signedIn) {
    // A slug link opened with a live session: go to the app page.
    if (path.kind === "slug") return path.nextPath || RedirectUri.home;
    return undefined;
  }

  if (path.kind === "bare" || path.kind === "slug") return null;
  // The same public pages as on the CPA host.
  const publicPaths: string[] = [
    RedirectUri.cpaPrivacyPolicy,
    RedirectUri.deleteCpaData,
    RedirectUri.appStoreSupport,
    RedirectUri.memberLogin,
  ];
  if (publicPaths.includes(urlPath)) return null;
  if (!rememberedSlug) return "/";
  const nextPath = getSafeNextPath(`${pathname}${search}`);
  return `/${rememberedSlug}${nextPath}`;
};

/** The signed-out login page on this host. */
export const getMemberDashboardHostLoginPath = (
  rememberedSlug: string = getRememberedMemberDashboardSlug()
): string => (rememberedSlug ? `/${rememberedSlug}` : "/");
